import React, { memo, useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, OverlayView, Polygon } from '@react-google-maps/api';
import { useGoogleMaps } from '@core/components/AddressMapView/useGoogleMaps';
import { TmRegionResponse } from '@business/entities/tm/TmRegionResponse';

export interface DealLocationMapProps {
  addressLat?: number | null;
  addressLng?: number | null;
  region?: TmRegionResponse | null; // the assigned rep's territory — highlighted + used to frame the view
  regions?: TmRegionResponse[]; // all sales-rep territories — drawn faded for context
  height?: number;
}

type LatLng = { lat: number; lng: number };

const REGION_COLOR = '#1E88E5';
const COLORS = ['#E53935', '#8E24AA', '#1E88E5', '#F4511E', '#039BE5', '#7CB342', '#FB8C00', '#00897B', '#D81B60'];

const MAP_OPTIONS: google.maps.MapOptions = {
  clickableIcons: false,
  streetViewControl: false,
  mapTypeControl: false,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }] },
  ],
};

type Territory = { id: string; name: string; paths: LatLng[][]; color: string; assigned: boolean };

function getCentroid(paths: LatLng[][]): LatLng {
  let lat = 0;
  let lng = 0;
  let count = 0;
  paths.forEach(path =>
    path.forEach(p => {
      lat += p.lat;
      lng += p.lng;
      count += 1;
    }),
  );
  return { lat: lat / count, lng: lng / count };
}

function geoToPaths(geoJson: any): LatLng[][] | null {
  const geometry = geoJson?.geometry;
  if (!geometry) return null;
  if (geometry.type === 'Polygon') {
    return [geometry.coordinates[0].map(([lng, lat]: [number, number]) => ({ lat: Number(lat), lng: Number(lng) }))];
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((poly: [number, number][][]) =>
      poly[0].map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) })),
    );
  }
  return null;
}

export const DealLocationMap = memo((props: DealLocationMapProps) => {
  const { addressLat, addressLng, region, regions, height = 260 } = props;
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const { isLoaded } = useGoogleMaps();

  const hasAddress = addressLat != null && addressLng != null;
  const containerStyle = useMemo(() => ({ width: '100%', height: `${height}px` }), [height]);

  // One entry per sales rep. Colors are keyed to the rep's position in the full list so a
  // territory keeps the same color whether or not it is the assigned one.
  const territories = useMemo(() => {
    const assignedId = region?.id != null ? String(region.id) : null;
    const list: Territory[] = [];
    (regions ?? []).forEach((r, idx) => {
      const paths = geoToPaths(r.geoJson);
      if (!paths) return;
      list.push({
        id: String(r.id),
        name: r.name,
        paths,
        color: COLORS[idx % COLORS.length],
        assigned: assignedId != null && String(r.id) === assignedId,
      });
    });
    // Inline-modal variant only passes the assigned rep's territory (no full list).
    if (assignedId && region?.geoJson && !list.some(t => t.assigned)) {
      const paths = geoToPaths(region.geoJson);
      if (paths) list.push({ id: assignedId, name: region.name, paths, color: REGION_COLOR, assigned: true });
    }
    return list;
  }, [regions, region]);

  const hasAssigned = useMemo(() => territories.some(t => t.assigned), [territories]);

  useEffect(() => {
    if (!mapInstance || !isLoaded) return;

    const bounds = new window.google.maps.LatLngBounds();
    let extended = false;

    if (hasAddress) {
      bounds.extend({ lat: addressLat, lng: addressLng });
      extended = true;
    }
    territories
      .filter(t => t.assigned)
      .forEach(t => t.paths.forEach(path => path.forEach(pt => { bounds.extend(pt); extended = true; })));
    if (!extended && territories.length) {
      territories.forEach(t => t.paths.forEach(path => path.forEach(pt => { bounds.extend(pt); extended = true; })));
    }

    if (extended) {
      mapInstance.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
      window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const z = mapInstance.getZoom() ?? 0;
        if (z > 14) mapInstance.setZoom(14);
      });
    }
  }, [isLoaded, mapInstance, hasAddress, addressLat, addressLng, territories]);

  if (!isLoaded) return null;
  if (!hasAddress && territories.length === 0) return null;

  return (
    <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid #dee2e6', marginBottom: 12 }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={10}
        center={hasAddress ? { lat: addressLat, lng: addressLng } : { lat: 39.5, lng: -98.35 }}
        onLoad={map => setMapInstance(map)}
        options={MAP_OPTIONS}
      >
        {territories.map(t =>
          t.paths.map((path, i) => (
            <Polygon
              key={`${t.id}-${i}`}
              path={path}
              options={{
                fillColor: t.color,
                strokeColor: t.color,
                // Assigned rep pops; once someone is assigned, the rest fade to background context.
                fillOpacity: t.assigned ? 0.3 : hasAssigned ? 0.1 : 0.15,
                strokeOpacity: t.assigned ? 1 : hasAssigned ? 0.55 : 0.9,
                strokeWeight: t.assigned ? 4 : hasAssigned ? 1.5 : 2,
                clickable: false,
                zIndex: t.assigned ? 3 : 1,
              }}
            />
          )),
        )}

        {territories.map(t => (
          <OverlayView
            key={`label-${t.id}`}
            position={getCentroid(t.paths)}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                display: 'inline-flex',
                width: 'max-content',
                alignItems: 'center',
                gap: 6,
                background: '#fff',
                border: t.assigned ? `2px solid ${t.color}` : '1px solid rgba(0,0,0,0.06)',
                borderRadius: 16,
                padding: '3px 9px',
                fontSize: 11,
                fontWeight: t.assigned ? 700 : 600,
                whiteSpace: 'nowrap',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                opacity: hasAssigned && !t.assigned ? 0.65 : 1,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
              <span style={{ color: '#1a1a1a' }}>{t.name}</span>
            </div>
          </OverlayView>
        ))}

        {hasAddress && <Marker position={{ lat: addressLat, lng: addressLng }} />}
      </GoogleMap>
    </div>
  );
});
